import { ChangeDetectionStrategy, Component, effect, inject } from '@angular/core';
import { AuthorizationService } from '@core/authorization/services/authorization.service';
import { ImpersonateBannerComponent } from '@layout/impersonate-banner/components/impersonate-banner/impersonate-banner.component';
import { SsoMappingRulesBannerComponent } from '@layout/sso-mapping-rules-banner/components/sso-mapping-rules-banner/sso-mapping-rules-banner.component';
import { SsoMappingRulesBannerFacade } from '@layout/sso-mapping-rules-banner/facades/sso-mapping-rules-banner.facade';
import { HeaderComponent } from '@layout/header/components/header/header.component';
import { LeftPanelComponent } from '@layout/left-panel/components/left-panel/left-panel.component';
import { RouterOutlet } from '@angular/router';
import { RouteDataService } from '@core/route-data/services/route-data.service';
import { PermissionsFacade } from '@core/permissions/facades/permissions.facade';
import { Feature, Permission } from '@kaseya-shared';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { FeaturesFacade } from '@core/features/facades/features.facade';

@Component({
  selector: 'itc-main-view',
  templateUrl: 'main-view.component.html',
  styleUrl: 'main-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ImpersonateBannerComponent, SsoMappingRulesBannerComponent, HeaderComponent, LeftPanelComponent, RouterOutlet],
})
export class MainViewComponent {
  private readonly routeDataService = inject(RouteDataService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly ssoMappingRulesBannerFacade = inject(SsoMappingRulesBannerFacade);
  private readonly permissionsFacade = inject(PermissionsFacade);
  private readonly featuresFacade = inject(FeaturesFacade);

  readonly showSsoMappingRulesBanner = this.ssoMappingRulesBannerFacade.showBanner;
  readonly fitsScreenWidth = toSignal(
    this.routeDataService.getDataWithTheDeepestProperty('fitsScreenWidth').pipe(map(value => value?.fitsScreenWidth))
  );
  readonly isLeftNavigationHidden = toSignal(
    this.routeDataService.getDataWithTheDeepestProperty('hideLeftNavigation').pipe(map(value => value?.hideLeftNavigation))
  );
  readonly isCustomRolesFeatureEnabled = this.featuresFacade.isFeatureEnabled(Feature.CustomRoles);

  constructor() {
    this.loadBannerWhenPermitted();
  }

  get isImpersonatedUser(): boolean {
    return this.authorizationService.isImpersonatedUser();
  }

  readonly hasAccessToThirdPartyIdentityProviderRead = this.permissionsFacade.hasPermission(Permission.ThirdPartyIdentityProviderRead);

  private loadBannerWhenPermitted(): void {
    effect(() => {
      if (this.hasAccessToThirdPartyIdentityProviderRead() && this.isCustomRolesFeatureEnabled()) {
        this.ssoMappingRulesBannerFacade.loadBannerVisibility();
      }
    });
  }
}
